/**
 * Pack judgment section (DC-1518, v3.0 Block 3) — the host layout inside
 * ContextualFitPanel: provenance header + RuleResultCard list + archetype
 * coverage strip + AI synthesis block + the override capture flow.
 *
 * The one design idea (UX spec §0): the deterministic rules ARE the judgment;
 * the AI narrative is commentary. This section renders ABOVE the existing fit
 * narrative, and the narrative block inside it is labeled and typographically
 * subordinate to the rule cards.
 *
 * Data flow: the `pack_judgment` summary from the CV run response carries the
 * full deterministic projection (rules, evidence, remediation, citations), so
 * the cards render even for deterministic-only runs that never persist a
 * judgment. When a judgment WAS persisted (judgment_id non-null), the section
 * also fetches GET /api/cv-judgments/:id for the override chain, effective
 * classification, and the pack subdomain vocabulary that seeds the override
 * modal. That fetch failing never takes the deterministic layer down — the
 * override affordance degrades to a muted retry line (fail-soft read side).
 *
 * UX spec: docs/design/v3.0-domain-pack-surfaces-ux-spec.md §§1, 5.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { RuleResultCard } from './RuleResultCard';
import { OverrideCaptureModal, type ClassificationOption } from './OverrideCaptureModal';
import {
  OUTCOME_SORT_ORDER,
  isRuleOutcome,
  type JudgmentDetailResponse,
  type JudgmentOverride,
  type PackJudgmentSummary,
} from './packJudgmentTypes';

const COVERAGE_VISIBLE_CAP = 3;
const COLLAPSE_THRESHOLD = 4;
const DIGEST_VISIBLE_CHARS = 12;

const SKIP_LINES: Record<string, string> = {
  'no-llm-provider':
    'AI synthesis unavailable (no provider configured) — deterministic results are complete and authoritative.',
  'no-dataset': 'Judgment not recorded — this analysis has no dataset identity.',
};

const SOURCE_LABELS: Record<string, string> = {
  'domain-binding': 'domain binding',
  'perspective-fallback': 'perspective fallback',
};

// ---------------------------------------------------------------------------
// Provenance digest — truncated, copy-on-click, announces "copied"
// ---------------------------------------------------------------------------

function DigestChip({ digest }: { digest: string }): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const short = digest.slice(0, DIGEST_VISIBLE_CHARS);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(digest);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (http, permissions) — the title still carries
      // the full digest, so nothing is lost.
    }
  };

  return (
    <button type="button" className="dp-digest" title={digest} onClick={copy} aria-live="polite">
      {copied ? 'copied' : `sha256:${short}…`}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Archetype coverage strip (§1.3) — a quantity, not a verdict
// ---------------------------------------------------------------------------

function CoverageStrip({ summary }: { summary: PackJudgmentSummary }): React.ReactElement | null {
  const [showAll, setShowAll] = useState(false);
  // Zero-archetype packs (all three built-ins) render nothing — the strip is
  // omitted entirely, not shown empty.
  if (summary.archetype_coverage.length === 0) return null;

  const visible = showAll
    ? summary.archetype_coverage
    : summary.archetype_coverage.slice(0, COVERAGE_VISIBLE_CAP);
  const hidden = summary.archetype_coverage.length - visible.length;

  return (
    <div className="dp-coverage" data-testid="dp-coverage">
      <span className="dp-coverage-label">Structural fit</span>
      {visible.map((entry) => (
        <span className="dp-coverage-item" key={entry.archetype_id}>
          <span className="dp-coverage-id">{entry.archetype_id}</span>
          <span
            className="dp-meter"
            role="img"
            aria-label={`coverage ${entry.coverage.toFixed(2)}`}
          >
            <span
              className="dp-meter-fill"
              style={{ width: `${Math.round(Math.min(1, Math.max(0, entry.coverage)) * 100)}%` }}
            />
          </span>
          <span className="dp-coverage-value">{entry.coverage.toFixed(2)}</span>
        </span>
      ))}
      {hidden > 0 && (
        <button type="button" className="dp-coverage-more" onClick={() => setShowAll(true)}>
          +{hidden} more
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export interface PackJudgmentSectionProps {
  /** The `pack_judgment` field from the CV run response; null → render nothing. */
  summary: PackJudgmentSummary | null;
}

type DetailState =
  | { status: 'idle' | 'loading' }
  | { status: 'loaded'; detail: JudgmentDetailResponse }
  | { status: 'error' };

export function PackJudgmentSection({ summary }: PackJudgmentSectionProps): React.ReactElement | null {
  const [detailState, setDetailState] = useState<DetailState>({ status: 'idle' });
  const [modalOpen, setModalOpen] = useState(false);
  // Local supersede chain — starts from the read-side, extends as overrides
  // are recorded through the modal (append-only, like the table underneath).
  const [overrides, setOverrides] = useState<JudgmentOverride[]>([]);

  const judgmentId = summary?.judgment_id ?? null;

  const fetchDetail = useCallback(async () => {
    if (!judgmentId) return;
    setDetailState({ status: 'loading' });
    try {
      const response = await fetch(`/api/cv-judgments/${judgmentId}`);
      if (!response.ok) throw new Error(`judgment fetch failed (${response.status})`);
      const detail = (await response.json()) as JudgmentDetailResponse;
      setDetailState({ status: 'loaded', detail });
      setOverrides(detail.overrides ?? []);
    } catch (err) {
      console.error('[PackJudgmentSection] judgment fetch error:', err);
      setDetailState({ status: 'error' });
    }
  }, [judgmentId]);

  useEffect(() => {
    setOverrides([]);
    if (judgmentId) {
      void fetchDetail();
    } else {
      setDetailState({ status: 'idle' });
    }
  }, [judgmentId, fetchDetail]);

  const sortedResults = useMemo(() => {
    if (!summary) return [];
    return [...summary.rule_results].sort((a, b) => {
      const aOrder = isRuleOutcome(a.outcome) ? OUTCOME_SORT_ORDER[a.outcome] : 99;
      const bOrder = isRuleOutcome(b.outcome) ? OUTCOME_SORT_ORDER[b.outcome] : 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.rule_id.localeCompare(b.rule_id);
    });
  }, [summary]);

  const modalOptions = useMemo<ClassificationOption[]>(() => {
    if (!summary) return [];
    const detail = detailState.status === 'loaded' ? detailState.detail : null;
    const subdomains: ClassificationOption[] = (detail?.pack_subdomains ?? []).map((s) => ({
      value: s.id,
      label: s.caption ? `${s.caption} (${s.id})` : s.id,
      group: 'subdomain',
    }));
    const archetypes: ClassificationOption[] = summary.archetype_coverage.map((a) => ({
      value: a.archetype_id,
      label: a.archetype_id,
      group: 'archetype',
    }));
    return [...subdomains, ...archetypes];
  }, [summary, detailState]);

  // State: no pack (legacy) — absence of the feature must not advertise
  // itself on legacy flows. No empty-state card.
  if (!summary) return null;

  const skipLine = summary.synthesis_skipped_reason
    ? SKIP_LINES[summary.synthesis_skipped_reason] ?? summary.synthesis_skipped_reason
    : null;

  const latestOverride = overrides.length > 0 ? overrides[overrides.length - 1] : null;
  const originalClassification = summary.classification;
  const effectiveClassification = latestOverride?.new_classification ?? originalClassification;
  const overridden = latestOverride !== null;
  const collapsed = sortedResults.length > COLLAPSE_THRESHOLD;

  const loadingDetail = judgmentId !== null && detailState.status === 'loading';
  const detailFailed = judgmentId !== null && detailState.status === 'error';
  const detailLoaded = detailState.status === 'loaded';

  const handleRecorded = (override: JudgmentOverride) => {
    // The supersede made visible: strikethrough + pill apply instantly —
    // state change, not theater (spec §6).
    setOverrides((prev) => [...prev, override]);
  };

  return (
    <div className="dp-section section" data-testid="pack-judgment-section" aria-label="Domain pack judgment">
      <div className="dp-eyebrow">Domain Pack Judgment</div>
      <div className="dp-body">
        {loadingDetail ? (
          <div className="dp-skeleton" data-testid="dp-skeleton" aria-label="Loading judgment record">
            <div className="dp-skeleton-line" />
            <div className="dp-skeleton-card" />
            <div className="dp-skeleton-card" />
            <div className="dp-skeleton-card" />
          </div>
        ) : (
          <>
            <div className="dp-provenance">
              <div className="dp-verdict">
                {originalClassification ? (
                  overridden ? (
                    <>
                      Classified:{' '}
                      <span className="dp-verdict-strike">{originalClassification}</span>{' '}
                      <span className="dp-verdict-corrected">{effectiveClassification}</span>
                      <span className="dp-overridden-pill">Overridden</span>
                    </>
                  ) : (
                    <>
                      Classified: {originalClassification}
                      {summary.confidence !== null && (
                        <span className="dp-verdict-confidence">
                          confidence {summary.confidence}
                        </span>
                      )}
                    </>
                  )
                ) : (
                  <>Deterministic rule results</>
                )}
              </div>
              <div className="dp-provenance-meta">
                <span>
                  {summary.pack_ref} · {SOURCE_LABELS[summary.pack_source] ?? summary.pack_source}
                </span>
                <DigestChip digest={summary.input_digest} />
                {judgmentId && detailLoaded && (
                  <button
                    type="button"
                    className="dp-override-btn"
                    onClick={() => setModalOpen(true)}
                  >
                    Override
                  </button>
                )}
              </div>
            </div>

            <div className="dp-cards">
              {sortedResults.map((result) => (
                <RuleResultCard
                  key={`${result.rule_id}@${result.rule_version}`}
                  result={result}
                  defaultCollapsed={collapsed}
                />
              ))}
            </div>

            <CoverageStrip summary={summary} />

            {detailFailed && (
              <p className="dp-retry-line" data-testid="dp-retry-line">
                Couldn&rsquo;t load the judgment record for override capture.
                <button type="button" onClick={() => void fetchDetail()}>
                  Retry
                </button>
              </p>
            )}

            {skipLine ? (
              <p className="dp-synthesis-skip" data-testid="dp-synthesis-skip">
                {skipLine}
              </p>
            ) : (
              summary.narrative && (
                <div className="dp-synthesis">
                  <span className="dp-synthesis-label">
                    <Sparkles size={12} aria-hidden="true" />
                    AI synthesis — interprets, cannot override, the rule results above
                  </span>
                  <p className="dp-synthesis-body">{summary.narrative}</p>
                </div>
              )
            )}
          </>
        )}
      </div>

      {judgmentId && (
        <OverrideCaptureModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          judgmentId={judgmentId}
          currentClassification={effectiveClassification ?? 'unclassified'}
          confidence={summary.confidence}
          packRef={summary.pack_ref}
          options={modalOptions}
          onRecorded={handleRecorded}
        />
      )}
    </div>
  );
}

export default PackJudgmentSection;
